import axiosInstance from 'Utils/axiosInstance';

const mapDataToUser = (data) => ({
    ...data,
    creation_date: new Date(data.creation_date),
});

export async function updateProfile(payload) {
    const response = await axiosInstance.patch('/users/me', payload);

    if (!response?.data) return null;

    return mapDataToUser(response.data);
}

export async function updateUser(payload) {
    const response = await axiosInstance.put('/users/me', payload);
    return response?.data;
}

export async function getUser() {
    const response = await axiosInstance.get('/users/me');

    if (!response?.data) return null;

    return mapDataToUser(response.data);
}

export async function sendUserEvent(event) {
    await axiosInstance.post('/users/me/log_event', event);
}

export async function deleteUserMe(reason) {
    await axiosInstance.delete('/users/me', { data: { reason } });
}

export async function verifyLoginWithGoogle(code) {
    return await axiosInstance.post('/auth/login_with_google', { code });
}

export async function sendOnboard(userOnboard) {
    const response = await axiosInstance.post('/users/me/onboard', userOnboard);
    return response?.data;
}

export async function sendOnboardProject(projectOnboard) {
    const response = await axiosInstance.post('/projects/selected/onboard', projectOnboard);
    return response?.data;
}

export async function getSubcriptionLink(params) {
    const response = await axiosInstance.post('/projects/selected/subscription', params);
    return response?.data;
}

export async function getSubscriptionModifyLink() {
    const response = await axiosInstance.put('/projects/selected/subscription');
    return response?.data;
}

export async function getOnboardingCheckoutLink(params) {
    const response = await axiosInstance.post('/projects/selected/onboarding', params);
    return response?.data;
}

export async function getProjectInvoices() {
    const response = await axiosInstance.get('/projects/selected/invoices');
    return response?.data;
}

export async function getProjectInvoiceDownloadUrl(invoiceId) {
    const response = await axiosInstance.get(`/projects/selected/invoices/${invoiceId}`);
    return response?.data;
}

export async function createUser(params) {
    const response = await axiosInstance.post('/users', params);

    if (!response) return null;

    const user = await getUser();
    if (!user) return null;

    return {
        ...user,
        login_response: response.data,
        is_new_user: response.status === 201,
    };
}

export async function reactivateUsersMe() {
    const response = await axiosInstance.post('/users/me/reactivate');

    if (!response?.data) return null;

    return mapDataToUser(response.data);
}

export async function updateUsersMeEmail(email, otp = '') {
    let requestBody = { email };

    if (otp !== '') {
        requestBody['otp'] = otp;
    }

    const response = await axiosInstance.put('/users/me/email', requestBody);

    if (!response?.data) return null;

    return mapDataToUser(response.data);
}

export async function linkUserAccount(token) {
    const response = await axiosInstance.post('/users/me/account', { token });

    if (!response?.data) return null;

    return mapDataToUser(response.data);
}

export async function unlinkUserAccount(name) {
    const response = await axiosInstance.delete('/users/me/account', { data: { name } });

    if (!response?.data) return null;

    return mapDataToUser(response.data);
}

/**
 * Projects
 */

function mapDtoToProject(data) {
    let projectData = {
        ...data,
        creation_date: new Date(data.creation_date),
    };

    // We need to ensure that data.subscription is not null. With the ... syntax
    // we are creating a subscription field only containing expiration_datetime and the code
    // in SettingsSubscription.js assumes it exists a subscription
    if (data.subscription) {
        projectData['subscription']['expiration_datetime'] = new Date(data.subscription.expiration_datetime);
    }

    return projectData;
}

export async function getProjectSelected() {
    const response = await axiosInstance.get('/projects/selected');

    if (!response?.data) return null;

    return mapDtoToProject(response.data);
}

export async function cancelProjectSelectedSubscription(survey) {
    const response = await axiosInstance.delete('/projects/selected/subscription', { data: survey });

    if (!response?.data) return null;

    return mapDtoToProject(response.data);
}

export async function reactivateProjectSelectedSubscription() {
    const response = await axiosInstance.post('/projects/selected/subscription/reactivate');

    if (!response?.data) return null;

    return mapDtoToProject(response.data);
}

export async function getProjects() {
    const response = await axiosInstance.get('/projects');

    if (!response?.data) return null;

    return response.data.map(mapDtoToProject);
}

export async function updateProjectSelected(payload) {
    const response = await axiosInstance.patch('/projects/selected', payload);

    if (!response?.data) return null;

    return mapDtoToProject(response.data);
}

export async function getProjectSelectedBilling() {
    const response = await axiosInstance.get('/projects/selected/billing');

    if (!response?.data) return null;

    return response.data;
}

export async function getProjectSelectedUsers() {
    const response = await axiosInstance.get('/projects/selected/users');

    if (!response?.data) return null;

    return response.data;
}

export async function getProjectSelectedAccessLogs(params = {}) {
    const response = await axiosInstance.get('/projects/selected/access_logs', { params });

    if (!response?.data) return null;

    return response.data;
}

export async function deleteProjectWorkspaceSelectedFakeData() {
    const response = await axiosInstance.delete('/projects/workspaces/selected/fake_data');

    if (!response?.data) return null;

    return response.data;
}

export async function getProjectSelectedWorkspaces() {
    const response = await axiosInstance.get('/projects/selected/workspaces');

    if (!response?.data) return null;

    return response.data;
}

export async function updateProjectSelectedBilling(payload) {
    const response = await axiosInstance.patch('/projects/selected/billing', payload);

    if (!response?.data) return null;

    return response.data;
}

export async function updateProjectSelectedStripeId(stripeId) {
    const response = await axiosInstance.patch('/projects/selected/stripe-id', { stripe_id: stripeId });

    if (!response?.data) return null;

    return response.data;
}

export async function getProjectWorkspacesSelected() {
    const response = await axiosInstance.get('/projects/workspaces/selected');

    if (!response?.data) return null;

    return response.data;
}

export async function updateProjectWorkspacesSelected(workspaceId, payload) {
    const response = await axiosInstance.patch(`/projects/selected/workspaces/${workspaceId}`, payload);

    if (!response?.data) return null;

    return response.data;
}

export async function connectTaxAuthority(taxAuthority, taxId, { province } = {}) {
    const response = await axiosInstance.post(`/projects/workspaces/selected/tax_authority`, {
        tax_authority: taxAuthority,
        tax_id: taxId,
        ...(province && { es_tbai_province: province }),
    });

    if (!response?.data) return null;

    return response.data;
}

export async function restartTaxAuthority() {
    const response = await axiosInstance.post(`/projects/workspaces/selected/tax_authority/restart`);

    if (!response?.data) return null;

    return response.data;
}

export async function createWorkspaceToProject(projectId, payload) {
    const response = await axiosInstance.post('/projects/workspaces', {
        project_id: projectId,
        ...payload,
    });

    if (!response?.data) return null;

    return response.data;
}

export async function createProjectWorkspace(payload) {
    const response = await axiosInstance.post('/projects/selected/workspaces', payload);

    if (!response?.data) return null;

    return response.data;
}

export async function changeProjectWorkspaceSelected(workspaceId) {
    const response = await axiosInstance.post('/projects/workspaces/selected', {
        set_project_workspace_id: workspaceId,
    });

    if (!response?.data) return null;

    return response.data;
}

export async function getInvitations() {
    const response = await axiosInstance.get('/users/me/invitations');

    if (!response?.data) return null;

    return response.data;
}

export async function updateInvitation(projectId, payload) {
    const response = await axiosInstance.put(`/users/me/invitations/${projectId}`, payload);

    if (!response?.data) return null;

    return response.data;
}

export async function inviteUser(payload) {
    const response = await axiosInstance.post('/projects/selected/users', payload);

    if (!response?.data) return null;

    return response.data;
}

export async function deleteWorkspace(id) {
    await axiosInstance.delete(`/projects/selected/workspaces/${id}`);
}

export async function createProject(payload) {
    const response = await axiosInstance.post('/projects', payload);

    if (!response?.data) return null;

    return mapDtoToProject(response.data);
}

export async function deleteUser(userId) {
    await axiosInstance.delete(`/projects/selected/users`, {
        data: {
            user_id: userId,
        },
    });
}

export async function createWorkspace(projectId, payload) {
    const response = await axiosInstance.post(`/projects/${projectId}/workspaces`, payload);

    if (!response?.data) return null;

    return response.data;
}

export async function joinProject(payload) {
    return axiosInstance.post('/projects/join', payload);
}

export async function getQuotas() {
    const response = await Promise.all([
        axiosInstance.get('/projects/quotas').then((r) => r.data),
        axiosInstance.get('/invoices/quotas').then((r) => ({
            invoices_issued_max: r.data.quota_issued_max,
            invoices_issued_used: r.data.quota_issued_used,
            invoices_scanned_max: r.data.quota_scanned_max,
            invoices_scanned_used: r.data.quota_scanned_used,
            storage_max: r.data.quota_storage_max,
            storage_used: r.data.quota_storage_used,
        })),
        axiosInstance.get('/connections/quotas').then((r) => r.data),
    ]);

    return response.reduce((acc, quotas) => ({ ...acc, ...quotas }), {});
}

export async function createUserSetting(payload) {
    const response = await axiosInstance.post('/users/me/settings', payload);

    if (!response?.data) return null;

    return response.data;
}

export async function getUserSettings() {
    const response = await axiosInstance.get('/users/me/settings');

    if (!response?.data) return null;

    const settings = Object.entries(response.data).reduce((acc, [key, value]) => {
        acc[key] = {
            ...value,
            creation_date: new Date(value.creation_date),
        };
        return acc;
    }, {});

    return settings;
}

export async function addUserToWorkspace(workspaceId, userId) {
    await axiosInstance.post(`/projects/selected/workspaces/${workspaceId}/users`, { user_id: userId });
}

export async function removeUserFromWorkspace(workspaceId, userId) {
    await axiosInstance.delete(`/projects/selected/workspaces/${workspaceId}/users`, {
        data: { user_id: userId },
    });
}

const mapDtoToWorkspace = (data) => ({
    ...data,
    creation_date: new Date(data.creation_date),
});

export async function getWorkspaceByProjectId(projectId) {
    const response = await axiosInstance.get(`/projects/${projectId}/workspaces`);

    if (!response?.data) return null;

    return response.data.map(mapDtoToWorkspace);
}

export async function banUser(email) {
    await axiosInstance.post('/users/ban', { email });
}

export async function banProjectSelected() {
    const response = await axiosInstance.post('/projects/selected/ban');

    if (!response?.data) return null;

    return response.data;
}

export async function editSubscriptionSupport(data) {
    return axiosInstance.put('/projects/selected/subscription/addons', data);
}

export async function createUserSupport(email) {
    const response = await axiosInstance.post('/users/support', { email });

    if (!response?.data) return null;

    return response.data;
}

export async function getWorkspaces() {
    const response = await axiosInstance.get('/projects/workspaces');

    if (!response?.data) return null;

    return response.data.map(mapDtoToWorkspace);
}

export async function refreshWorkspace() {
    const response = await axiosInstance.post('/projects/workspaces/selected/refresh');

    if (!response?.data) return null;

    return response.data;
}

export async function setNewAdmin(userId) {
    const response = await axiosInstance.post('/projects/selected/admin', { user_id: userId });

    if (!response?.data) return null;

    return response.data;
}

export async function completeWorkspaceStep(step) {
    await axiosInstance.post('/projects/workspaces/selected/step', { step });
}
